import React, { useState } from "react";
import {
  Box,
  Button,
  CloseButton,
  Drawer,
  Group,
  Tabs,
  Title,
  UnstyledButton,
} from "@mantine/core";
import {
  ReportMetricDef,
  ReportV2Definition,
  ViewConfig,
} from "../../../../types/reportsV2";
import { ViewPatch } from "../hooks/useReportDraft";
import ColumnsTab from "./ColumnsTab";
import GroupByTab from "./GroupByTab";
import TotalsTab from "./TotalsTab";
import styles from "./CustomizeDrawer.module.css";

type TabKey = "group" | "columns" | "totals";

export interface CustomizeDrawerProps {
  opened: boolean;
  onClose: () => void;
  definition: ReportV2Definition;
  /** Static metrics plus the latest run's payment-method columns. */
  catalog: ReportMetricDef[];
  view: ViewConfig;
  onUpdate: (patch: ViewPatch) => void;
  /** Phones: a full-screen sheet with arrow reordering instead of the
   *  docked side panel with drag and drop. */
  mobile?: boolean;
}

const TITLE = "Customize view";

const RESETS: Record<
  TabKey,
  { label: string; patch: (d: ReportV2Definition) => Partial<ViewConfig> }
> = {
  // The sort survives (it applies at every level); a column reset that
  // hides the sorted column drops it (useReportDraft keeps sorts valid).
  group: { label: "Clear groupings", patch: () => ({ groups: [] }) },
  columns: {
    label: "Reset columns",
    patch: (d) => ({ columns: [...d.default_view.columns] }),
  },
  totals: {
    label: "Reset totals",
    patch: (d) => ({ kpis: [...d.default_view.kpis] }),
  },
};

/**
 * "Customize view": Group by / Columns / Summary totals. Edits apply to the
 * draft view immediately, so the table updates as you go; Save view in the
 * header keeps them.
 *
 * Desktop: a panel docked beside the report (the canvas lays it out). The
 * report narrows to make room instead of being covered, so every row and
 * column stays reachable while customizing. Phones: a full-screen sheet
 * whose selected lists reorder with up / down arrows. Same rules either way.
 */
export default function CustomizeDrawer({
  opened,
  onClose,
  mobile = false,
  ...bodyProps
}: CustomizeDrawerProps) {
  if (mobile) {
    return (
      <Drawer
        opened={opened}
        onClose={onClose}
        position="bottom"
        size="100%"
        title={TITLE}
        closeOnClickOutside={false}
        classNames={{
          content: `${styles.drawer} ${styles.fullScreen}`,
          body: styles.drawerBody,
          title: styles.drawerTitle,
        }}
      >
        <CustomizeBody {...bodyProps} onClose={onClose} mobile />
      </Drawer>
    );
  }
  if (!opened) return null;
  return (
    <Box
      component="aside"
      aria-label={TITLE}
      className={`${styles.drawer} ${styles.docked}`}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key !== "Escape" || e.defaultPrevented) return;
        // Escape during a keyboard drag cancels the move, not the panel.
        // dnd-kit handles it on the document (after this handler), but marks
        // the handle being dragged aria-pressed.
        if ((e.target as HTMLElement).closest('[aria-pressed="true"]')) return;
        onClose();
      }}
    >
      <Group className={styles.dockedHeader} wrap="nowrap">
        <Title order={2} className={styles.drawerTitle}>
          {TITLE}
        </Title>
        <CloseButton aria-label="Close customize view" onClick={onClose} />
      </Group>
      <CustomizeBody {...bodyProps} onClose={onClose} mobile={false} />
    </Box>
  );
}

type CustomizeBodyProps = Omit<CustomizeDrawerProps, "opened" | "mobile"> & {
  mobile: boolean;
};

/** The tabs and the Reset / Done footer, shared by the panel and the sheet. */
function CustomizeBody({
  onClose,
  definition,
  catalog,
  view,
  onUpdate,
  mobile,
}: CustomizeBodyProps) {
  const [tab, setTab] = useState<TabKey>("group");
  const reset = RESETS[tab];
  const tabProps = { definition, view, onUpdate, mobile };

  return (
    <>
      <Tabs
        value={tab}
        onChange={(value) => value && setTab(value as TabKey)}
        variant="pills"
        keepMounted={false}
        classNames={{ list: styles.tabList, tab: styles.tab }}
        className={styles.tabs}
      >
        <Tabs.List grow>
          <Tabs.Tab value="group">Group by</Tabs.Tab>
          <Tabs.Tab value="columns">Columns</Tabs.Tab>
          <Tabs.Tab value="totals">Summary totals</Tabs.Tab>
        </Tabs.List>
        <Box className={styles.scroll}>
          <Tabs.Panel value="group">
            <GroupByTab {...tabProps} />
          </Tabs.Panel>
          <Tabs.Panel value="columns">
            <ColumnsTab {...tabProps} catalog={catalog} />
          </Tabs.Panel>
          <Tabs.Panel value="totals">
            <TotalsTab {...tabProps} catalog={catalog} />
          </Tabs.Panel>
        </Box>
      </Tabs>
      <Box className={styles.footer}>
        <UnstyledButton
          type="button"
          className={styles.resetButton}
          onClick={() => onUpdate(reset.patch(definition))}
        >
          {reset.label}
        </UnstyledButton>
        <Box className={styles.footerSpacer} />
        <Button color="teal" onClick={onClose}>
          Done
        </Button>
      </Box>
    </>
  );
}
